// Moteur de contrôle du « Journal de Paie » Cegid.
// Recalcule, pour chaque salarié, ce qui est vérifiable à partir des seules colonnes du journal :
//   Ret. Sal.      = CNSS (plafonnée) + AMO + cotisations complémentaires éventuelles
//   Ret. Pat.      = CNSS patronale (plafonnée) + AF + FP + AMO patronale + complémentaire éventuelle
//   Net imposable  = Brut − Ret. Sal. − Frais professionnels
//   Net à payer    = Brut abattu − Ret. Sal. + Divers plus − Divers moins
//   IR théorique   = barème(Net imposable) — l'IR n'a pas de colonne dédiée, il est inclus dans « Divers moins »
// Tous les paramètres sont éditables ; les défauts dépendent de l'année de la période.

import { DEFAULT_BAREME_IR, baremeIR } from './netBrutEngine';

// Barème IR mensuel en vigueur jusqu'au 31/12/2024 — [seuil_haut, taux, déduction]
export const BAREME_IR_2024 = [
  [2500.01,  0.00, 0      ],
  [4166.68,  0.10, 250    ],
  [5000.01,  0.20, 666.67 ],
  [6666.68,  0.30, 1166.67],
  [15000.01, 0.34, 1433.33],
  [Infinity, 0.38, 2033.33],
];

// SMIG horaire (DH) par date d'effet — à ajuster si besoin dans les paramètres
const SMIG_HORAIRE = [
  ['2019-07-01', 14.13],
  ['2022-09-01', 14.81],
  ['2023-09-01', 15.55],
  ['2025-01-01', 17.10],
  ['2026-01-01', 17.92],
];

export const SEVERITY = {
  erreur: { label: 'Erreur', bg: 'danger', rank: 3 },
  alerte: { label: 'Alerte', bg: 'warning', rank: 2 },
  info:   { label: 'Info',   bg: 'info',    rank: 1 },
};

export function defaultParams(periodeDebut) {
  const d = periodeDebut || new Date().toISOString().slice(0, 10);
  const year = Number(d.slice(0, 4));
  const smig = SMIG_HORAIRE.filter(([from]) => from <= d).pop();
  return {
    cnss_taux: 0.0448,
    cnss_plafond: 6000,
    amo_taux: 0.0226,
    compl_sal_taux: 0,          // cotisations complémentaires salarié (CIMR, mutuelle…) — auto-détectées
    cnss_pat_taux: 0.0898,
    pat_non_plafonne_taux: 0.064 + 0.016 + 0.0411, // AF + FP + AMO patronale
    compl_pat_taux: 0,          // complémentaire patronale — auto-détectée
    // Frais professionnels : 35 % / 25 % depuis 2023, 20 % avant
    fraispro_taux: year >= 2023 ? 0.35 : 0.20,
    fraispro_plafond: 2500,
    fraispro_seuil: year >= 2023 ? 6500 : Infinity,
    fraispro_taux2: 0.25,
    fraispro_plafond2: 2916.67,
    bareme: year >= 2025 ? 'LF2025' : 'ANCIEN',
    charge_famille_montant: year >= 2026 ? 50 : year >= 2025 ? 41.67 : 30,
    charge_famille_max: 6,
    smig_horaire: smig ? smig[1] : 14.13,
    tolerance: 0.05,            // écart toléré (DH) sur les montants recalculés
    seuil_variation: 0.20,      // variation brut/net vs mois précédent signalée au-delà de 20 %
  };
}

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const cnssBase = (brut, p) => Math.min(brut, p.cnss_plafond);
const retSalBase = (brut, p) => cnssBase(brut, p) * p.cnss_taux + brut * p.amo_taux;
const retPatBase = (brut, p) => cnssBase(brut, p) * p.cnss_pat_taux + brut * p.pat_non_plafonne_taux;

export const fraisPro = (brut, p) => (brut <= p.fraispro_seuil
  ? Math.min(brut * p.fraispro_taux, p.fraispro_plafond)
  : Math.min(brut * p.fraispro_taux2, p.fraispro_plafond2));

const baremeOf = (p) => (p.bareme === 'LF2025' ? DEFAULT_BAREME_IR : BAREME_IR_2024);

// Taux complémentaire le plus fréquent du journal (arrondi à 0,01 %) — ignoré s'il est marginal
function modeRate(values) {
  const counts = new Map();
  for (const v of values) {
    const k = Math.round(v * 10000) / 10000;
    if (k <= 0.0005) continue;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  let best = 0, bestN = 0;
  for (const [k, n] of counts) if (n > bestN) { best = k; bestN = n; }
  return bestN >= 2 ? best : 0;
}

// Ajuste les taux complémentaires (salarié / patronal) sur le journal importé
export function calibrate(lignes, params) {
  const sample = lignes.filter((l) => l.brut > 500);
  return {
    ...params,
    compl_sal_taux: modeRate(sample.map((l) => (l.ret_sal - retSalBase(l.brut, params)) / l.brut)),
    compl_pat_taux: modeRate(sample.map((l) => (l.ret_pat - retPatBase(l.brut, params)) / l.brut)),
  };
}

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (n) => `${(n * 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;

// Détermine le profil de cotisation (base / base + complémentaire) qui explique le montant observé
function matchProfile(observed, base, brut, complTaux, tol) {
  if (Math.abs(observed - base) <= tol) return { profil: 'base', attendu: base };
  if (complTaux > 0 && Math.abs(observed - (base + brut * complTaux)) <= tol) return { profil: 'compl', attendu: base + brut * complTaux };
  return { profil: null, attendu: complTaux > 0 ? base + brut * complTaux : base, tauxImplicite: brut ? (observed - base) / brut : 0 };
}

export function controlerLigne(l, p, ctx) {
  const anomalies = [];
  const add = (code, severity, message) => anomalies.push({ code, severity, message });
  const tol = p.tolerance;
  const brut = l.brut || 0;

  // 1. Identité du net à payer
  const netCalcule = r2(l.brut_abattu - l.ret_sal + l.divers_plus - l.divers_moins);
  const ecartNet = r2(l.net_a_payer - netCalcule);
  if (Math.abs(ecartNet) > 0.02) {
    add('NET', 'erreur', `Net à payer ${fmt(l.net_a_payer)} ≠ Brut abattu − Ret. Sal. + Divers plus − Divers moins = ${fmt(netCalcule)} (écart ${fmt(ecartNet)})`);
  }

  // 2. Retenues salariales / patronales
  const sal = matchProfile(l.ret_sal, retSalBase(brut, p), brut, p.compl_sal_taux, tol);
  const pat = matchProfile(l.ret_pat, retPatBase(brut, p), brut, p.compl_pat_taux, tol);
  if (brut > 0 && !sal.profil) {
    add('RET_SAL', 'erreur', `Ret. Sal. ${fmt(l.ret_sal)} ≠ attendu ${fmt(sal.attendu)} (taux complémentaire implicite ${pct(sal.tauxImplicite)})`);
  }
  if (brut > 0 && !pat.profil) {
    add('RET_PAT', 'erreur', `Ret. Pat. ${fmt(l.ret_pat)} ≠ attendu ${fmt(pat.attendu)} (taux complémentaire implicite ${pct(pat.tauxImplicite)})`);
  }
  if (sal.profil && pat.profil && sal.profil !== pat.profil) {
    add('PROFIL', 'alerte', sal.profil === 'compl'
      ? 'Cotisation complémentaire salariale prélevée sans part patronale'
      : 'Cotisation complémentaire patronale sans part salariale');
  }

  // 3. Net imposable
  const fp = fraisPro(brut, p);
  const niAttendu = r2(brut - l.ret_sal - fp);
  const ecartNI = r2(l.net_imposable - niAttendu);
  if (brut > 0 && Math.abs(ecartNI) > tol) {
    add('NET_IMPOSABLE', 'erreur', `Net imposable ${fmt(l.net_imposable)} ≠ Brut − Ret. Sal. − Frais pro (${fmt(fp)}) = ${fmt(niAttendu)} (écart ${fmt(ecartNI)})`);
  }

  // 4. IR théorique — inclus dans « Divers moins » avec les autres retenues et la reprise d'arrondi
  const irBareme = Math.max(0, baremeIR(l.net_imposable, baremeOf(p)));
  const reductionMax = p.charge_famille_montant * p.charge_famille_max;
  const irMin = Math.max(0, irBareme - reductionMax);
  if (irMin > 0 && l.divers_moins < irMin - 1) {
    add('IR', 'alerte', `Divers moins ${fmt(l.divers_moins)} < IR minimal ${fmt(irMin)} (barème ${fmt(irBareme)} − ${p.charge_famille_max} charges de famille) : IR possiblement non retenu`);
  }
  const autresRetenues = r2(l.divers_moins - irBareme);

  // 5. Contrôles de cohérence
  if (l.assujet) add('ASSUJET', 'alerte', `Assujettissement manuel de ${fmt(l.assujet)} — à justifier`);
  if (l.divers_moins < 0) add('DIVERS_MOINS_NEG', 'alerte', `Divers moins négatif (${fmt(l.divers_moins)}) : régularisation en faveur du salarié`);
  if (l.divers_plus >= 1) add('DIVERS_PLUS', 'info', `Divers plus de ${fmt(l.divers_plus)} (au-delà d'un simple arrondi)`);
  if (brut > 0 && !l.heures) add('HEURES', 'info', 'Heures non renseignées');
  if (l.heures > 0 && brut / l.heures < p.smig_horaire - 0.005) {
    add('SMIG', 'alerte', `Taux horaire ${fmt(brut / l.heures)} DH < SMIG horaire ${fmt(p.smig_horaire)} DH`);
  }
  if (Math.abs(l.net_a_payer - Math.round(l.net_a_payer)) > 0.001) add('ARRONDI', 'info', 'Net à payer non arrondi au dirham');
  if (l.date_sortie && ctx.periodeDebut && l.date_sortie < ctx.periodeDebut && l.net_a_payer > 0) {
    add('SORTI', 'alerte', `Salarié sorti le ${frDate(l.date_sortie)} mais payé ${fmt(l.net_a_payer)}`);
  }
  if (l.date_entree && ctx.periodeFin && l.date_entree > ctx.periodeFin) {
    add('ENTREE', 'erreur', `Date d'entrée ${frDate(l.date_entree)} postérieure à la période`);
  }
  if (brut === 0 && l.net_a_payer === 0) add('VIDE', 'info', 'Bulletin à zéro');
  if (ctx.doublons.has(l.matricule)) add('DOUBLON', 'erreur', 'Matricule présent plusieurs fois dans le journal');

  // 6. Comparaison avec le mois précédent
  let prev = null;
  if (ctx.prevMap) {
    prev = ctx.prevMap.get(l.matricule) || null;
    if (!prev) {
      const entreDansPeriode = l.date_entree && ctx.periodeDebut && l.date_entree >= ctx.periodeDebut;
      add('NOUVEAU', entreDansPeriode ? 'info' : 'alerte', entreDansPeriode
        ? 'Nouvel entrant de la période'
        : 'Absent du journal précédent alors qu\'entré avant la période');
    } else {
      const varBrut = prev.brut ? (brut - prev.brut) / prev.brut : 0;
      const varNet = prev.net_a_payer ? (l.net_a_payer - prev.net_a_payer) / prev.net_a_payer : 0;
      if (Math.abs(varBrut) > p.seuil_variation) add('VAR_BRUT', 'alerte', `Brut ${varBrut > 0 ? '+' : ''}${pct(varBrut)} vs mois précédent (${fmt(prev.brut)} → ${fmt(brut)})`);
      else if (Math.abs(varNet) > p.seuil_variation) add('VAR_NET', 'alerte', `Net ${varNet > 0 ? '+' : ''}${pct(varNet)} vs mois précédent (${fmt(prev.net_a_payer)} → ${fmt(l.net_a_payer)})`);
    }
  }

  const severity = anomalies.reduce((m, a) => (SEVERITY[a.severity].rank > (SEVERITY[m]?.rank || 0) ? a.severity : m), null);
  return {
    ...l,
    profilSal: sal.profil,
    profilPat: pat.profil,
    retSalAttendu: r2(sal.attendu),
    retPatAttendu: r2(pat.attendu),
    indemnitesNonImposables: r2(l.brut_abattu - brut),
    fraisPro: r2(fp),
    niAttendu,
    irBareme: r2(irBareme),
    autresRetenues,
    netCalcule,
    coutEmployeur: r2(l.brut_abattu + l.ret_pat),
    prev,
    anomalies,
    severity,
  };
}

export const TOTAL_COLS = [
  ['heures', 'Heures'],
  ['brut', 'Brut'],
  ['brut_abattu', 'Brut abattu'],
  ['ret_sal', 'Ret. Sal.'],
  ['ret_pat', 'Ret. Pat.'],
  ['assujet', 'Assujet.'],
  ['net_imposable', 'Net imposable'],
  ['divers_plus', 'Divers plus'],
  ['divers_moins', 'Divers moins'],
  ['net_a_payer', 'Net à payer'],
];

export function controlerJournal(journal, params) {
  const lignes = journal.lignes || [];
  const counts = new Map();
  lignes.forEach((l) => counts.set(l.matricule, (counts.get(l.matricule) || 0) + 1));
  const doublons = new Set([...counts].filter(([, n]) => n > 1).map(([m]) => m));
  const prevMap = journal.precedent ? new Map(journal.precedent.lignes.map((l) => [l.matricule, l])) : null;
  const ctx = { periodeDebut: journal.periode_debut, periodeFin: journal.periode_fin, doublons, prevMap };

  const rows = lignes.map((l) => controlerLigne(l, params, ctx));

  // Salariés du mois précédent absents de ce journal
  const current = new Set(lignes.map((l) => l.matricule));
  const sortants = journal.precedent
    ? journal.precedent.lignes.filter((l) => !current.has(l.matricule)).map((l) => ({
      ...l,
      justifie: !!(l.date_sortie && journal.periode_debut && l.date_sortie <= journal.periode_debut),
    }))
    : [];

  // Totaux : somme des lignes vs « TOTAL GENERAL » imprimé
  const totaux = TOTAL_COLS.map(([k, label]) => {
    const somme = r2(lignes.reduce((s, l) => s + (Number(l[k]) || 0), 0));
    const journalVal = journal.totaux ? journal.totaux[k] : null;
    return { key: k, label, somme, journal: journalVal, ecart: journalVal == null ? null : r2(somme - journalVal) };
  });
  const nbJournal = journal.totaux?.nb_salaries ?? null;

  const bySeverity = { erreur: 0, alerte: 0, info: 0 };
  rows.forEach((r) => { if (r.severity) bySeverity[r.severity] += 1; });
  const byCode = {};
  rows.forEach((r) => r.anomalies.forEach((a) => { byCode[a.code] = (byCode[a.code] || 0) + 1; }));

  const sum = (k) => r2(rows.reduce((s, r) => s + (Number(r[k]) || 0), 0));
  return {
    rows,
    sortants,
    totaux,
    nbJournal,
    nbLignes: lignes.length,
    bySeverity,
    byCode,
    kpis: {
      brut: sum('brut'),
      net: sum('net_a_payer'),
      retSal: sum('ret_sal'),
      retPat: sum('ret_pat'),
      coutEmployeur: sum('coutEmployeur'),
      irBareme: sum('irBareme'),
      prevNet: journal.precedent ? r2(journal.precedent.lignes.reduce((s, l) => s + (Number(l.net_a_payer) || 0), 0)) : null,
    },
  };
}

export const ANOMALY_LABELS = {
  NET: 'Net à payer incohérent',
  RET_SAL: 'Retenues salariales',
  RET_PAT: 'Charges patronales',
  PROFIL: 'Profil de cotisation',
  NET_IMPOSABLE: 'Net imposable',
  IR: 'IR insuffisant',
  ASSUJET: 'Assujettissement manuel',
  DIVERS_MOINS_NEG: 'Divers moins négatif',
  DIVERS_PLUS: 'Divers plus',
  HEURES: 'Heures absentes',
  SMIG: 'Sous le SMIG',
  ARRONDI: 'Net non arrondi',
  SORTI: 'Payé après sortie',
  ENTREE: 'Entrée après période',
  VIDE: 'Bulletin à zéro',
  DOUBLON: 'Doublon matricule',
  NOUVEAU: 'Nouveau vs M-1',
  VAR_BRUT: 'Variation brut',
  VAR_NET: 'Variation net',
};

export function frDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const periodeLabel = (iso) => (iso ? `${MOIS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}` : '—');
